import { computed } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useMasterDataQuery } from "../../api";

/**
 * Provides computed helpers for master data, similar to the old masterData store
 * Uses TanStack Query for data fetching
 */
export function useMasterDataHelpers() {
	const {
		data: masterData,
		isPending: loading,
		isError,
		error,
	} = useQuery(useMasterDataQuery());

	// User-related getters
	const currentUser = computed(() => masterData.value?.userInfo?.user || null);
	const userRole = computed(() => masterData.value?.userInfo?.role || null);
	const userId = computed(() => masterData.value?.userInfo?.user?.id || null);
	const displayName = computed(
		() => masterData.value?.userInfo?.user?.displayName || null
	);
	const bookingGroups = computed(
		() => masterData.value?.userInfo?.bookingGroups || []
	);

	// Buildings and rooms getters
	const buildingsList = computed(() => {
		if (!masterData.value?.buildings?.list) return [];
		return Object.values(masterData.value.buildings.list);
	});

	const roomsByBuilding = computed(() => {
		if (!masterData.value?.buildings?.list) return {};
		return masterData.value.buildings.list;
	});

	const allRooms = computed(() => {
		if (!masterData.value?.buildings?.list) return [];
		return Object.values(masterData.value.buildings.list).flatMap(
			(building) => building.rooms || []
		);
	});

	const getRoomById = computed(() => (roomId) => {
		if (!masterData.value?.buildings?.list) return null;

		for (const building of Object.values(masterData.value.buildings.list)) {
			const room = building.rooms?.find((room) => room.id === roomId);
			if (room) {
				return {
					...room,
					building: {
						id: building.id,
						name: building.name,
						short: building.short,
					},
				};
			}
		}
		return null;
	});

	const getBuildingById = computed(() => (buildingId) => {
		return buildingsList.value.find((building) => building.id === buildingId);
	});

	// Equipment getters
	const equipmentsList = computed(() => {
		if (!masterData.value?.equipments?.list) return [];
		return Object.values(masterData.value.equipments.list);
	});

	const equipmentsById = computed(() => {
		if (!masterData.value?.equipments?.list) return {};
		return masterData.value.equipments.list;
	});

	const getEquipmentById = computed(() => (equipmentId) => {
		return equipmentsById.value[equipmentId] || null;
	});

	// Room types getters
	const roomTypesList = computed(() => {
		if (!masterData.value?.roomTypes?.list) return [];
		return Object.values(masterData.value.roomTypes.list);
	});

	// Get room types that are present in rooms the user can book
	const bookableRoomTypesList = computed(() => {
		const bookableRoomTypeIds = new Set();

		// Get all rooms the user can book
		allRooms.value.forEach((room) => {
			if (room?.permissions?.canCreateRegular && room?.roomTypes) {
				room.roomTypes.forEach((roomTypeId) => {
					bookableRoomTypeIds.add(parseInt(roomTypeId));
				});
			}
		});

		// Filter room types to only include those from bookable rooms
		return roomTypesList.value.filter((roomType) =>
			bookableRoomTypeIds.has(parseInt(roomType.id))
		);
	});

	const roomTypesById = computed(() => {
		if (!masterData.value?.roomTypes?.list) return {};
		return masterData.value.roomTypes.list;
	});

	const getRoomTypeById = computed(() => (roomTypeId) => {
		return roomTypesById.value[roomTypeId] || null;
	});

	// Booking types getters
	const bookingTypesList = computed(() => {
		if (!masterData.value?.bookingTypes?.list) return [];
		return Object.values(masterData.value.bookingTypes.list);
	});

	const bookingTypesById = computed(() => {
		if (!masterData.value?.bookingTypes?.list) return {};
		return masterData.value.bookingTypes.list;
	});

	const getBookingTypeById = computed(() => (typeId) => {
		return bookingTypesById.value[typeId] || null;
	});

	const defaultBookingType = computed(() => {
		return bookingTypesList.value.find((type) => type.studentDefault) || null;
	});

	// Universal permissions getters
	const canViewOtherBookings = computed(
		() => masterData.value?.permissions?.canViewOtherBookings || false
	);
	const canManageOtherBookings = computed(
		() => masterData.value?.permissions?.canManageOtherBookings || false
	);
	const canBookWithoutTransaction = computed(
		() => masterData.value?.permissions?.canBookWithoutTransaction || false
	);
	const canIgnoreOpeningHours = computed(
		() => masterData.value?.permissions?.canIgnoreOpeningHours || false
	);
	const canCreateSeries = computed(
		() => masterData.value?.permissions?.canCreateSeries || false
	);

	// Room-specific permissions - Critical for calendar-configs
	const canCreateInRoom = computed(() => (roomId) => {
		const room = getRoomById.value(roomId);
		return room?.permissions?.canCreateRegular || false;
	});

	// Room-specific walk-in permission - rooms permissioned only at the walk-in tier
	// (no regular/without-transaction permission) still need to be selectable for students
	// during the walk-in window
	const canCreateWalkInInRoom = computed(() => (roomId) => {
		const room = getRoomById.value(roomId);
		return room?.permissions?.canCreateWalkIn || false;
	});

	// Derived permissions based on universal permissions

	const canBookForOthers = computed(() => canManageOtherBookings.value);

	// Helper function to get room equipment details
	const getRoomEquipments = computed(() => (room) => {
		if (!room?.equipments || !Array.isArray(room.equipments)) return [];
		return room.equipments
			.map((equipmentId) => getEquipmentById.value(equipmentId))
			.filter(Boolean);
	});

	// Helper function to get room type details
	const getRoomTypes = computed(() => (room) => {
		if (!room?.roomTypes || !Array.isArray(room.roomTypes)) return [];
		return room.roomTypes
			.map((roomTypeId) => getRoomTypeById.value(roomTypeId))
			.filter(Boolean);
	});

	// Get all unique seat counts from available rooms
	const availableSeatCounts = computed(() => {
		const seatCounts = new Set();
		allRooms.value.forEach((room) => {
			if (typeof room.seats === "number" && room.seats > 0) {
				seatCounts.add(room.seats);
			}
		});
		return Array.from(seatCounts).sort((a, b) => a - b);
	});

	// Helper function to get building with rooms and their equipment details
	const getBuildingWithDetails = computed(() => (buildingId) => {
		const building = getBuildingById.value(buildingId);
		if (!building) return null;

		return {
			...building,
			rooms:
				building.rooms?.map((room) => ({
					...room,
					equipmentDetails: getRoomEquipments.value(room),
					roomTypeDetails: getRoomTypes.value(room),
				})) || [],
		};
	});

	// Student config getters - for booking time constraints
	const studentConfig = computed(() => masterData.value?.studentConfig || null);
	const earliestBookingSeconds = computed(
		() => masterData.value?.studentConfig?.earliestStartInFutureInSeconds || 900
	);
	const latestBookingSeconds = computed(
		() =>
			masterData.value?.studentConfig?.latestStartInFutureInSeconds || 1209600
	);
	const modificationDeadlineSeconds = computed(
		() => masterData.value?.studentConfig?.modificationDeadlineInSeconds || 3600
	);

	// Status getters
	const isLoaded = computed(() => masterData.value !== undefined);
	const isLoading = computed(() => loading.value);
	const hasError = computed(() => isError.value);

	return {
		// Raw data
		masterData,

		// Status
		isLoaded,
		isLoading,
		hasError,
		error,

		// User getters
		currentUser,
		userRole,
		userId,
		displayName,
		bookingGroups,

		// Buildings and rooms getters
		buildingsList,
		roomsByBuilding,
		allRooms,
		getRoomById,
		getBuildingById,

		// Equipment getters
		equipmentsList,
		equipmentsById,
		getEquipmentById,
		getRoomEquipments,

		// Room types getters
		roomTypesList,
		bookableRoomTypesList,
		roomTypesById,
		getRoomTypeById,
		getRoomTypes,

		// Seat count helpers
		availableSeatCounts,

		// Booking types getters
		bookingTypesList,
		bookingTypesById,
		getBookingTypeById,
		defaultBookingType,

		// Universal permissions getters
		canViewOtherBookings,
		canManageOtherBookings,
		canBookWithoutTransaction,
		canIgnoreOpeningHours,
		canCreateInRoom, // Critical for calendar-configs
		canCreateWalkInInRoom,

		// Derived permissions
		canCreateSeries,
		canBookForOthers,

		// Helper getters
		getBuildingWithDetails,

		// Student config getters
		studentConfig,
		earliestBookingSeconds,
		latestBookingSeconds,
		modificationDeadlineSeconds,
	};
}
